// frontend/src/modules/reports/pages/workforce/OvertimePage.tsx
//
// Phase 4 (Reports Frontend), Task 2. Overtime minutes per employee, bucketed
// by day or week — follows the HoursSummaryPage reference pattern: ReportShell
// owns date range + CSV chrome, this page owns fetching (useApi keyed on the
// resolved range + group-by/employee filters), a total-overtime stat tile, a
// bar visual (existing design-system chart primitive), and a basis-aware
// table.
//
// `basis` honesty rule (same as HoursSummaryPage/ExceptionsPage): "Scheduled"
// must relabel to "Company hours" when the tenant has scheduling off.

import * as React from 'react';
import { TimerReset } from 'lucide-react';
import { ReportShell, type ReportRange } from '../../components/ReportShell';
import { StatCard } from '../../../../design-system/components/StatCard';
import { DataTable } from '../../../../design-system/components/DataTable';
import { HighlightedBarChart } from '../../../../design-system/charts';
import { useApi } from '../../../../design-system/useApi';
import { reportUrl, downloadReportCsv } from '../../api';
import type { OvertimeReport } from '../../types';
import { VISUAL } from '../../../../design-system/visual';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../../../components/ui/select';
import type { EmployeeWithUser } from '../../../hr/types';

type OvertimeGroupBy = 'day' | 'week';
const GROUP_BYS: OvertimeGroupBy[] = ['day', 'week'];

/** Bars the chart will draw before the 3-letter labels stop being readable. */
const CHART_ROW_LIMIT = 24;

/** '125' minutes -> '2h 5m'. */
function fmtHours(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h}h ${m}m`;
}

export function OvertimePage() {
  const [range, setRange] = React.useState<ReportRange | null>(null);
  const [groupBy, setGroupBy] = React.useState<OvertimeGroupBy>('day');
  const [employeeId, setEmployeeId] = React.useState<string>('all');

  const empResult = useApi<{ employees: EmployeeWithUser[] }>('/api/hr/employees');
  const employees = empResult.data?.employees ?? [];

  const path = range
    ? reportUrl('overtime', {
      from: range.from,
      to: range.to,
      groupBy,
      employeeId: employeeId !== 'all' ? employeeId : undefined,
    })
    : null;
  const result = useApi<{ report: OvertimeReport }>(path, [groupBy, employeeId]);
  const forbidden = result.status === 403;

  const report = result.data?.report ?? null;
  const rows = report?.rows ?? [];
  const basis = report?.basis;
  const scheduledLabel = basis === 'company_hours' ? 'Company hours' : 'Scheduled';

  const totalOvertime = React.useMemo(
    () => rows.reduce((s, r) => s + r.overtime_minutes, 0),
    [rows],
  );

  const chartData = React.useMemo(
    () => rows.slice(0, CHART_ROW_LIMIT).map((r) => ({
      label: r.employee_name.split(' ')[0]?.slice(0, 3) ?? r.employee_name.slice(0, 3),
      value: r.overtime_minutes,
      valueLabel: fmtHours(r.overtime_minutes),
      highlight: r.overtime_minutes > 0,
    })),
    [rows],
  );

  return (
    <ReportShell
      eyebrow="Reports / Workforce"
      title="Overtime"
      description="Minutes worked beyond expectation per employee, bucketed by day or week."
      onRangeChange={setRange}
      access={!forbidden}
      accessDescription={forbidden ? (result.error ?? undefined) : undefined}
      loading={!forbidden && (range === null || result.loading)}
      error={forbidden ? null : result.error}
      onRetry={result.reload}
      isEmpty={!forbidden && range !== null && !result.loading && !result.error && rows.length === 0}
      filters={
        <>
          <div className="inline-flex items-center gap-1 p-1 rounded-full bg-muted/50 ring-1 ring-border/40">
            {GROUP_BYS.map((g) => (
              <button
                key={g}
                type="button"
                onClick={() => setGroupBy(g)}
                className={[
                  'px-3.5 h-8 rounded-full text-[12px] font-semibold capitalize transition-colors',
                  groupBy === g ? 'bg-saffron text-zinc-950' : 'text-muted-foreground hover:text-foreground',
                ].join(' ')}
              >
                {g}
              </button>
            ))}
          </div>
          {employees.length > 0 && (
            <Select value={employeeId} onValueChange={setEmployeeId}>
              <SelectTrigger className="h-9 rounded-full bg-card/80 border-border/60 px-3.5 text-[12.5px] font-semibold w-auto min-w-[180px]">
                <SelectValue placeholder="All employees" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All employees</SelectItem>
                {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </>
      }
      csv={range ? {
        filename: `overtime-${range.from}-to-${range.to}.csv`,
        onDownload: () => downloadReportCsv(
          'overtime',
          { from: range.from, to: range.to, groupBy, employeeId: employeeId !== 'all' ? employeeId : undefined },
          `overtime-${range.from}-to-${range.to}.csv`,
        ),
      } : null}
    >
      {report && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <StatCard label="Total overtime" value={fmtHours(totalOvertime)} icon={<TimerReset />} />
          </div>
          {chartData.length > 0 && (
            <div className={[VISUAL.bento, 'overflow-hidden p-6'].join(' ')}>
              <HighlightedBarChart data={chartData} />
              {/* The chart caps at CHART_ROW_LIMIT bars so the labels stay legible.
                  Unsaid, that reads as "these are all the rows" — say it instead. */}
              {rows.length > CHART_ROW_LIMIT && (
                <p className="mt-4 text-[12.5px] text-muted-foreground">
                  Showing the first {CHART_ROW_LIMIT} of {rows.length} rows. The table below is complete.
                </p>
              )}
            </div>
          )}
          <div className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
            <DataTable
              rows={rows}
              rowKey={(r) => `${r.employee_id}-${r.bucket}`}
              columns={[
                { key: 'employee', header: 'Employee', cell: (r) => <span className="font-medium text-foreground">{r.employee_name}</span> },
                { key: 'bucket', header: 'Period', cell: (r) => r.bucket },
                { key: 'scheduled', header: scheduledLabel, cell: (r) => fmtHours(r.scheduled_minutes) },
                { key: 'active', header: 'Active', cell: (r) => fmtHours(r.active_minutes) },
                { key: 'overtime', header: 'Overtime', cell: (r) => fmtHours(r.overtime_minutes) },
              ]}
            />
          </div>
        </>
      )}
    </ReportShell>
  );
}
